/**
 * `orange-webview-bar` — the MyOrange chrome. Prototype `webviewBar()`.
 *
 * Replaces the masthead, the breadcrumbs and the footer when the flow is opened
 * inside the MyOrange app (`?context=webview`). The bar is 48px with 44px targets,
 * a back control, the screen title and a close control.
 *
 * ### The close control has no defined behaviour
 *
 * In the real app the ✕ hands control back to MyOrange, and what MyOrange does with
 * a half-finished migration is not specified anywhere (Q-38) — nor is whether the
 * draft survives, nor whether the customer is warned first. The component emits
 * `closeRequested` and the shell does nothing with it but say so; inventing a
 * bridge protocol or a confirmation dialog here would put a made-up integration
 * contract in front of reviewers.
 *
 * The title is plain text, not a heading: the screen's own `<h1>` is the document's
 * heading, and a second one in the chrome would give every page two.
 */
import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { IconComponent } from '../../ui/icon/icon.component';

@Component({
  selector: 'orange-webview-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent],
  template: `
    <div class="wv-bar ground-inverse">
      <button type="button" [attr.aria-label]="backLabel" (click)="backRequested.emit()">
        <orange-icon name="back" size="sm" />
      </button>

      <span class="title">{{ title() }}</span>

      <button type="button" [attr.aria-label]="closeLabel" (click)="closeRequested.emit()">
        <span aria-hidden="true">✕</span>
      </button>
    </div>
  `,
  styles: [
    `
      .wv-bar {
        background: var(--surface-inverse);
        color: var(--surface);
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--space-2);
        padding: var(--space-2) var(--space-3);
        min-height: 48px;
        font-size: 11px;
        line-height: 16px;
        font-weight: 700;
      }

      button {
        background: none;
        border: 0;
        color: var(--surface);
        cursor: pointer;
        padding: var(--space-2);
        min-height: 44px;
        min-width: 44px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      /* The title yields before the two controls do — they are the only way out. */
      .title {
        flex: 1 1 auto;
        text-align: center;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    `,
  ],
})
export class WebviewBarComponent {
  readonly title = input.required<string>();

  readonly backRequested = output<void>();
  readonly closeRequested = output<void>();

  readonly backLabel = $localize`:@@webview.back:Înapoi`;
  readonly closeLabel = $localize`:@@webview.close:Închide`;
}
